import { useEffect, useState, useCallback, useRef, useContext, useMemo } from 'react';
import { useTaskAudioPlayer } from 'students/views/shared/bundles/audio/hooks';
import { tTaskAudioManagerItems } from 'students/views/shared/bundles/audio/models';

import { PreferencesContext } from 'students/views/shared/providers/Preferences';
import { GameAudioIds, gameSoundEffectAudioItems } from './audioItems';

export const useContainerDimensions = (
  myRef: React.RefObject<HTMLElement>,
  monitorResize = false
): { width: number; height: number } => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const getDimensions = () => ({
      width: myRef.current?.offsetWidth || 0,
      height: myRef.current?.offsetHeight || 0
    });
    const handleResize = () => {
      if (myRef.current && monitorResize) setDimensions(getDimensions());
    };
    if (myRef.current) setDimensions(getDimensions());
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myRef, monitorResize]);
  return dimensions;
};

export const useHintVisibility = (
  shouldAppear = true,
  hintTimeout = 5000
): [boolean, (timeout?: number) => void] => {
  const [showHint, setShowHint] = useState(false);
  const { hintsEffects } = useContext(PreferencesContext);
  const hintWasShownRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const hintTimeoutRef = useRef(hintTimeout);

  const updateLastInteractionTime = useCallback(
    (timeout = 5000) => {
      hintTimeoutRef.current = timeout;
      showHint && setShowHint(false);
    },
    [showHint]
  );

  useEffect(() => {
    timeoutRef.current && clearTimeout(timeoutRef.current);

    if (!(shouldAppear && hintsEffects) || hintWasShownRef.current) return undefined;

    timeoutRef.current = window.setTimeout(() => {
      setShowHint(true);
    }, hintTimeoutRef.current);

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [shouldAppear, hintsEffects]);

  useEffect(() => {
    if (showHint) {
      hintWasShownRef.current = true;
    }
  }, [showHint]);

  return [showHint, updateLastInteractionTime];
};

export interface IGameAudioPlayer {
  playIncorrectSound: () => void;
  playCorrectSound: () => void;
}

/**
 * Registers the shared sound effects itself, so the returned players work in
 * every game, including games that pass their own background music.
 * `extraAudioItems` must be a stable module-level constant — the registration
 * effect in `useTaskAudioPlayer` keys off the array identity.
 */
export function useGameAudioPlayer(
  extraAudioItems?: tTaskAudioManagerItems
): IGameAudioPlayer {
  const audioItems = useMemo(
    () =>
      extraAudioItems
        ? [...gameSoundEffectAudioItems, ...extraAudioItems]
        : gameSoundEffectAudioItems,
    [extraAudioItems]
  );

  const { playById } = useTaskAudioPlayer(audioItems);

  const playCorrectSound = useCallback(() => {
    playById(GameAudioIds.correct);
  }, [playById]);

  const playIncorrectSound = useCallback(() => {
    playById(GameAudioIds.incorrect);
  }, [playById]);

  return { playCorrectSound, playIncorrectSound };
}
